import React, { useState, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from "@mui/material";
import {
  Button,
  FileUploader,
  Iconography,
  IconographySizes,
  Loader,
  LoaderSizes,
} from "@hid-galaxy-ui/galaxy-react";
import { useAppSelector, useAppDispatch } from "../../../../../../../../redux";
import {
  getMediaFilesSagaAction,
  uploadMediaFilesSagaAction,
  deleteMediaFileSagaAction,
} from "../../../../../../../../redux/saga/project-validation/saga-actions";
import { MediaFile } from "../../../../../../../../redux/slices/project-validation/Types";
import { getFileViewUrlSagaAction } from "../../../../../../../../redux/saga/onboarding-form/saga-actions";
import {
  getUserListSagaAction,
  getUserListHIDSagaAction,
} from "../../../../../../../../redux/saga/user/saga-actions";
import DeleteModal from "../../../../../../../../common/components/DeleteModal";
import "./FilesSection.scss";

interface FilesSectionProps {
  isHIDUser: boolean;
  integrationId?: string;
  /** When false, upload and delete are disabled (validation_submission permission). */
  allowFileMutations?: boolean;
  /** TEMPORARY: data-entry view enabled for kiran. Remove with TEMPORARY block. */
  isDataEntryUserView?: boolean;
  /** TEMPORARY: uploaded_by user ID from Partner Name dropdown. Remove with TEMPORARY block. */
  dataEntryUploadedByUserId?: number;
  /** TEMPORARY: created_at (YYYY-MM-DD) from Submitted on datepicker. Remove with TEMPORARY block. */
  dataEntryCreatedAt?: string;
}

const FilesSection: React.FC<FilesSectionProps> = ({
  isHIDUser,
  integrationId,
  allowFileMutations = true,
  isDataEntryUserView = false,
  dataEntryUploadedByUserId,
  dataEntryCreatedAt,
}) => {
  const dispatch = useAppDispatch();
  const { loggedUserDetail } = useAppSelector((state) => state.auth);
  const { mediaFiles, mediaFilesStatus } = useAppSelector(
    (state) => state.project_validation
  );
  const { userList, userListHID, fetchedUserListPartnerID } = useAppSelector(
    (state) => state.user
  );
  const { partnerDetails } = useAppSelector((state) => state.partner_details);
  const [isUploadOpen, setIsUploadOpen] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [fileToDelete, setFileToDelete] = useState<{
    id: number;
    name: string;
  } | null>(null);

  // Format file size to human-readable format
  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + " " + sizes[i];
  };

  const formatDate = (dateString: string): string => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString();
    } catch {
      return dateString;
    }
  };

  const getUserName = (userId: number): string => {
    // Search in HID user list first
    const hidUser = userListHID.find((user) => user.ID === userId);
    if (hidUser) {
      return `${hidUser.first_name} ${hidUser.last_name}`.trim();
    }

    // Search in partner user list
    const partnerUser = userList.find((user) => user.ID === userId);
    if (partnerUser) {
      return `${partnerUser.first_name} ${partnerUser.last_name}`.trim();
    }

    return userId.toString();
  };

  useEffect(() => {
    if (integrationId) {
      dispatch(getMediaFilesSagaAction(integrationId));
    }
  }, [integrationId, dispatch]);

  useEffect(() => {
    if (userListHID.length === 0) {
      dispatch(getUserListHIDSagaAction());
    }

    if (
      partnerDetails?.ID &&
      fetchedUserListPartnerID !== partnerDetails.ID &&
      userList.length === 0
    ) {
      dispatch(getUserListSagaAction(partnerDetails.ID));
    }
  }, [
    dispatch,
    partnerDetails?.ID,
    fetchedUserListPartnerID,
    userList.length,
    userListHID.length,
  ]);

  const handleDeleteFile = (fileId: number, fileName: string) => {
    if (!allowFileMutations) return;
    setFileToDelete({ id: fileId, name: fileName });
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = () => {
    if (fileToDelete !== null) {
      dispatch(
        deleteMediaFileSagaAction({
          mediaId: fileToDelete.id,
          setDeleteModalOpen: setIsDeleteModalOpen,
          setFileToDelete: setFileToDelete,
        })
      );
    }
  };

  // Store selected files when FileUploader selects them
  const handleFileSelection = (e: any, data: any) => {
    if (!data || data.length === 0) {
      setSelectedFiles([]);
      return;
    }

    // Extract File objects from FileUploader data
    const files: File[] = [];
    data.forEach((fileData: any) => {
      if (fileData.fileDetails && fileData.fileDetails instanceof File) {
        files.push(fileData.fileDetails);
      }
    });

    setSelectedFiles(files);
  };

  // Upload files when button is clicked
  const handleUploadFiles = () => {
    if (!allowFileMutations) return;
    if (!isUploadOpen) {
      // If upload zone is closed, open it
      setIsUploadOpen(true);
      return;
    }

    // If upload zone is open and files are selected, upload them
    if (selectedFiles.length === 0) {
      return;
    }

    if (!integrationId) {
      return;
    }

    const baseUserId = loggedUserDetail?.ID;
    // TEMPORARY: for data-entry user, use selected partner user ID when available; otherwise fallback to logged-in user ID.
    const effectiveUserId =
      isDataEntryUserView && dataEntryUploadedByUserId
        ? dataEntryUploadedByUserId
        : baseUserId;
    if (!effectiveUserId) {
      return;
    }

    dispatch(
      uploadMediaFilesSagaAction(
        integrationId,
        selectedFiles,
        effectiveUserId,
        isDataEntryUserView ? dataEntryCreatedAt : undefined
      )
    );
    setSelectedFiles([]);
    setIsUploadOpen(false);
  };

  const handleFileRemoval = (e: any, data: Array<any>, deletedFile?: File) => {
    setSelectedFiles(data);
  };

  return (
    <div className="files-section">
      <div className="files-header">
        <h3 className="files-title">Files ({mediaFiles.length})</h3>
      </div>

      {mediaFilesStatus.error && (
        <div
          style={{ color: "#dc2626", marginBottom: "16px", fontSize: "14px" }}
        >
          {mediaFilesStatus.error}
        </div>
      )}

      <TableContainer component={Paper} className="files-table">
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>File name</TableCell>
              <TableCell>File size</TableCell>
              <TableCell>Upload date</TableCell>
              <TableCell>Uploaded by</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {mediaFilesStatus.loading && (
              <TableRow>
                <TableCell colSpan={5}>
                  <div className="uploading-row">
                    <Loader variant={LoaderSizes.medium} />
                    <span>Loading files...</span>
                  </div>
                </TableCell>
              </TableRow>
            )}
            {mediaFilesStatus.uploading && (
              <TableRow>
                <TableCell colSpan={5}>
                  <div className="uploading-row">
                    <Loader variant={LoaderSizes.medium} />
                    <span>Uploading...</span>
                  </div>
                </TableCell>
              </TableRow>
            )}
            {!mediaFilesStatus.loading && mediaFiles.length === 0 && (
              <TableRow>
                <TableCell
                  colSpan={5}
                  align="center"
                  style={{ padding: "24px" }}
                >
                  No files uploaded yet
                </TableCell>
              </TableRow>
            )}
            {!mediaFilesStatus.loading &&
              mediaFiles.map((file: MediaFile) => (
                <TableRow key={file.ID}>
                  <TableCell>
                    <span
                      className="file-name"
                      onClick={() =>
                        dispatch(getFileViewUrlSagaAction(file.file_path))
                      }
                    >
                      {file.file_name}
                    </span>
                  </TableCell>
                  <TableCell>{formatFileSize(file.size_bytes)}</TableCell>
                  <TableCell>{formatDate(file.created_at)}</TableCell>
                  <TableCell>{getUserName(file.uploaded_by)}</TableCell>
                  <TableCell align="center">
                    <Button
                      variant="flat"
                      icon={
                        <Iconography
                          icon="trashCan"
                          size={IconographySizes.Small}
                        />
                      }
                      onClick={() => handleDeleteFile(file.ID, file.file_name)}
                      className="delete-btn"
                      disabled={
                        !allowFileMutations ||
                        mediaFilesStatus.deleting === file.ID
                      }
                    />
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </TableContainer>
      {isUploadOpen && (
        <div className="upload-close-btn">
          <Button
            variant="flat"
            label="Close"
            normalCase={true}
            icon={<Iconography icon="xmark" size={IconographySizes.Small} />}
            onClick={() => {
              setIsUploadOpen(false);
              setSelectedFiles([]);
            }}
          />
        </div>
      )}
      {allowFileMutations && isUploadOpen && (
        <div className="upload-zone">
          <div className="upload-zone-content">
            <Iconography
              icon="cloudArrowUp"
              size={IconographySizes.Large}
              className="upload-icon"
            />
            <p className="upload-text">Choose a file or drag & drop it here</p>
            <p className="upload-formats">
              JPEG, PNG, PDF, and MP4 formats, up to 50MB
            </p>
            <FileUploader
              buttonConfig={{ label: "Browse files" }}
              isMultiple={true}
              onUpload={handleFileSelection}
              onRemove={handleFileRemoval}
              validateConfig={[
                {
                  maxFileSize: {
                    maxSizeInMb: 50,
                    message: "File exceeds 50MB size limit.",
                  },
                },
              ]}
            />
            {selectedFiles.length > 0 && (
              <p
                style={{
                  marginTop: "16px",
                  fontSize: "14px",
                  color: "#666666",
                }}
              >
                {selectedFiles.length} file(s) selected
              </p>
            )}
          </div>
        </div>
      )}

      <div className="files-footer">
        <Button
          variant="primary"
          label={
            isUploadOpen && selectedFiles.length > 0
              ? "Upload selected files"
              : "Upload files"
          }
          normalCase={true}
          icon={<Iconography icon="arrowUpFromBracket" />}
          onClick={handleUploadFiles}
          disabled={
            !allowFileMutations ||
            !integrationId ||
            (isUploadOpen && selectedFiles.length === 0) ||
            // TEMPORARY: for data-entry user, require Partner Name and Submitted on before enabling upload.
            (isDataEntryUserView &&
              (!dataEntryUploadedByUserId || !dataEntryCreatedAt))
          }
        />
      </div>

      {isDeleteModalOpen && fileToDelete && (
        <DeleteModal
          setDeleteOpen={setIsDeleteModalOpen}
          onDeleteButtonClick={handleConfirmDelete}
          deleteButtonLoading={mediaFilesStatus.deleting === fileToDelete.id}
          optionalHeader="Delete File"
          message={`Are you sure you want to delete "${fileToDelete.name}"? This action cannot be undone.`}
          info={{
            icon: "file",
            name: fileToDelete.name,
          }}
        />
      )}
    </div>
  );
};

export default FilesSection;
